import { LockOpenRounded } from "@mui/icons-material";
import { Grid, Tab, Tabs, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import { controlKeys, entityKeys } from "../../../../api/queryKeys";
import ExcludeOrIncludeButton from "../../../../components/buttons/ExcludeOrIncludeButton";
import ResizableDrawer from "../../../../components/ResizableDrawer";
import { Toast } from "../../../../components/Toast";
import MitigationControlsTable from "../../../posture/steps/components/MitigationControlsTable";

function ControlsDrawer({
  entity,
  controlsQuery,
  openDrawer,
  setOpenDrawer,
  relatedControlIds,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [exclusionFilter, setExclusionFilter] = useState("included");
  const [checkedControls, setCheckedControls] = useState([]);
  const {
    isEntityExcludedInControls,
    excludeOrIncludeEntityInControls,
    trackEvent,
  } = useAPI();
  const queryClient = useQueryClient();
  const colors = useColors();
  const tabStyle = {
    "&.Mui-selected": {
      boxShadow: "0px 0px 15px 2px rgba(0,0,0,0.2)", // Example boxShadow
    },
    borderRadius: "8px 8px 0 0", // Rounded top corners
    textTransform: "none",
    minHeight: "48px", // Match Material-UI Tab height or adjust as needed
    // Add more styles here to better match Chrome's tabs
  };
  useEffect(() => {
    let controlsData = controlsQuery?.data;

    if (controlsData) {
      const isQueryCompleted =
        controlsQuery.status === "success" || controlsQuery.status === "error";
      if (isQueryCompleted) {
        setIsLoading(false);
      }
      if (controlsQuery.status === "error")
        Toast.error("Failed to request controls");
    }
  }, [controlsQuery]);

  const {
    data: entityExclusionInControls,
    isLoading: isLoadingEntityExclusionInControls,
  } = useQuery({
    queryKey: controlKeys.entityExcludedInControls(
      entity?.entity_id,
      relatedControlIds
    ),
    queryFn: () =>
      isEntityExcludedInControls(entity.entity_id, relatedControlIds),
    enabled: relatedControlIds?.length > 0,
    select: (response) => response.data,
  });

  const [rowsControls, setRowsControls] = useState([]);
  const [controlsExcludingTheEntity, setControlsExcludingTheEntity] = useState(
    []
  );
  const [controlsIncludingTheEntity, setControlsIncludingTheEntity] = useState(
    []
  );

  useEffect(() => {
    if (isLoadingEntityExclusionInControls) return;

    let controlsData = controlsQuery?.data;

    if (controlsData) {
      let rowsControls = [];
      let controlsExcludingTheEntity = [];
      let controlsIncludingTheEntity = [];
      const relatedControls = controlsQuery?.data.map((data) => ({
        ...data,
        id: data.control_id,
      }));

      if (controlsQuery?.status === "success") {
        let controlIdsExcludingTheEntity =
          entityExclusionInControls
            ?.filter((controlIsExcluded) => controlIsExcluded.is_excluded)
            ?.map((controlIsExcluded) => controlIsExcluded.control_id) || [];
        controlsExcludingTheEntity = relatedControls.filter((control) =>
          controlIdsExcludingTheEntity.includes(control.control_id)
        );

        let controlIdsIncludingTheEntity =
          entityExclusionInControls
            ?.filter((controlIsExcluded) => !controlIsExcluded.is_excluded)
            ?.map((controlIsExcluded) => controlIsExcluded.control_id) || [];
        controlsIncludingTheEntity = relatedControls.filter((control) =>
          controlIdsIncludingTheEntity.includes(control.control_id)
        );

        rowsControls =
          exclusionFilter === "included"
            ? controlsIncludingTheEntity
            : controlsExcludingTheEntity;
      }

      setRowsControls(rowsControls);
      setControlsExcludingTheEntity(controlsExcludingTheEntity);
      setControlsIncludingTheEntity(controlsIncludingTheEntity);
    }
  }, [
    controlsQuery,
    exclusionFilter,
    entityExclusionInControls,
    isLoadingEntityExclusionInControls,
  ]);

  const onRowClick = (control_id) => {
    window.open(`/posture/controls?control_id=${control_id}`, "_blank");
  };

  const onDrawerClose = () => {
    searchParams.delete("gaps");
    setSearchParams(searchParams.toString());
  };
  const handleExcludeIncludeClick = async (reason = null) => {
    const is_excluding = exclusionFilter === "included";
    const successMessage = `The Entity is ${
      is_excluding ? "excluded from" : "included in"
    } the selected controls`;
    const errorMessage = `Failed to ${
      is_excluding ? "exclude the entity from" : "include the entity in"
    } the selected controls`;

    trackEvent(
      `User is ${is_excluding ? "excluding" : "including"} an entity ${
        is_excluding ? "from" : "to"
      } ${checkedControls.length} controls: ${checkedControls.join(", ")}`
    ).then();

    try {
      await excludeOrIncludeEntityInControls(
        entity?.entity_id,
        checkedControls,
        is_excluding,
        reason
      );
      Toast.success(successMessage);

      queryClient.invalidateQueries({
        queryKey: controlKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: entityKeys.entitiesByType(entity?.entity_type),
      });
    } catch (error) {
      Toast.error(errorMessage);
    }
  };

  const handleCheckedControls = (newCheckedControls) => {
    setCheckedControls(newCheckedControls);
  };

  return (
    <ResizableDrawer
      initialWidth={780}
      minWidth={780}
      PaperProps={{
        style: {
          overflowY: "auto",
          borderRadius: "8px 0 0 0",
          padding: 3,
        },
      }}
      anchor="right"
      open={openDrawer}
      onClose={() => {
        setOpenDrawer(false);
        onDrawerClose();
      }}
    >
      <Grid
        container
        direction="column"
        sx={{
          flexWrap: "nowrap",
          padding: 3,
          gap: 2,
          backgroundColor: colors.grey_50,
        }}
      >
        <Grid item>
          <Grid container>
            <LockOpenRounded sx={{ width: "35px", height: "35px" }} />
            <Typography variant="h3">Gaps</Typography>
          </Grid>
        </Grid>
        <Grid item>
          <Typography variant="subtitle1">{entity?.display_name}</Typography>
        </Grid>
        <Grid
          container
          display="flex"
          justifyContent="space-between"
          width={"100%"}
          flexDirection={"column"}
          sx={{
            boxShadow: " 0px 0px 2px 1px rgba(203, 203, 203, 0.50)",
            background: "white",
            borderRadius: "10px",
          }}
        >
          <Grid item>
            <Grid container alignItems={"center"} spacing={2}>
              <Grid item>
                <Tabs
                  sx={{
                    ".MuiTabs-indicator": {
                      display: "none",
                      boxShadow: "0px 0px 15px 2px " + colors.wizard_box_shadow,
                    },
                  }}
                  value={exclusionFilter}
                  onChange={(event, newValue) => {
                    setExclusionFilter(newValue);
                  }}
                >
                  <Tab
                    sx={{
                      ...tabStyle, // Spread your existing tab styles
                    }}
                    label={`Included (${controlsIncludingTheEntity.length})`}
                    value="included"
                    style={{ textTransform: "none" }}
                  />
                  <Tab
                    sx={{
                      ...tabStyle, // Spread your existing tab styles
                    }}
                    label={`Excluded (${controlsExcludingTheEntity.length})`}
                    value="excluded"
                    style={{ textTransform: "none" }}
                  />
                </Tabs>
              </Grid>
              <Grid item>
                <Grid
                  container
                  direction="row"
                  gap={1}
                  justifyContent={"flex-end"}
                >
                  <ExcludeOrIncludeButton
                    is_excluded={exclusionFilter === "excluded"}
                    disabled={checkedControls.length === 0}
                    toolTipText={{
                      exclude: "Exclude from selected gaps",
                      include: "Include in selected gaps",
                    }}
                    handleExclusion={(reason) =>
                      handleExcludeIncludeClick(reason)
                    }
                    sx={{ width: "34px" }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <MitigationControlsTable
            mitigatingControls={rowsControls}
            onRowClick={(control_id) => {
              onRowClick(control_id);
            }}
            loading={isLoading}
            boxSXProps={{
              border: "none",
              width: "100%",
              ".MuiDataGrid-root": {
                border: "none",
                boxShadow: "none",
              },
              ".MuiDataGrid-main": {},
            }}
            checkbox={true}
            handleCheckedControls={handleCheckedControls}
            pageSizeOptionsOverride={[10, 15, 20]}
            filterColumns={[
              "is_detected",
              "control_id",
              "name_for_gap_state",
              "tool",
              "nist_function",
              "severity",
            ]}
          />
        </Grid>
      </Grid>
    </ResizableDrawer>
  );
}

export default ControlsDrawer;
